import { createInput, createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

export const POSITIETABEL_EDIT_SELECTOR = '.cds-lrn-positietabel';

interface PositietabelConfig {
  type: string;
  rows: number;
  withColors: boolean;
  withAlt: boolean;
  checkboxes: Record<string, boolean>;
}

export function positietabelDialog(
  insertHtml: (html: string) => void,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const dialog = newDialog('Positietabel maken', () => insertHtml(''), mountTarget);

  const selectType = createSelect('type', 'Type', [
    { label: 'Positietabel', value: 'position' },
    { label: 'Massa', value: 'weight' },
    { label: 'Lengte', value: 'length' },
    { label: 'Oppervlakte', value: 'surface' },
    { label: 'Inhoud', value: 'content' },
    { label: 'Volume', value: 'volume' },
  ]);
  dialog.querySelector('#cds-dialog-content').append(selectType.field);

  const withColors = createInput('withColors', 'Met kleuren', 'checkbox');
  const withAlt = createInput('withAlt', 'Met alternatieve benaming', 'checkbox');

  const rows = createInput('rows', 'Aantal rijen', 'number');

  const checkboxGroup = document.createElement('div');
  checkboxGroup.classList.add('flex', 'flex-wrap', 'gap-m', 'items-center');

  dialog.querySelector('#cds-dialog-content').append(checkboxGroup, rows.field, withColors.field);

  let inputFields = [];
  const rebuildCheckboxes = (type: string, checkboxState: Record<string, boolean> = {}) => {
    checkboxGroup.innerHTML = '';
    inputFields = createContentMaker(type);
    inputFields.forEach((input) => {
      checkboxGroup.append(input.field);
      if (checkboxState[input.field.id]) input.setChecked(true);
    });
    if (['length', 'surface'].includes(type)) {
      dialog.querySelector('#cds-dialog-content').append(withAlt.field);
    } else {
      withAlt.field.remove();
    }
  };
  selectType.field.addEventListener('change', function () {
    rebuildCheckboxes(selectType.value());
  });

  if (existingEl) {
    const cfg = readConfig<PositietabelConfig>(existingEl, { type: '', rows: 0, withColors: false, withAlt: false, checkboxes: {} });
    if (cfg.type) {
      selectType.setValue(cfg.type);
      rebuildCheckboxes(cfg.type, cfg.checkboxes || {});
    }
    if (cfg.rows) rows.setValue(cfg.rows);
    withColors.setChecked(!!cfg.withColors);
    withAlt.setChecked(!!cfg.withAlt);
  }

  const getCheckboxState = () =>
    inputFields.reduce((acc, input) => {
      acc[input.field.id] = input.checked();
      return acc;
    }, {});

  dialog.addEventListener('onSubmit', () => {
    const config: PositietabelConfig = {
      type: selectType.value(),
      rows: +rows.value(),
      withColors: withColors.checked(),
      withAlt: withAlt.checked(),
      checkboxes: getCheckboxState(),
    };
    insertHtml(createTable(config.checkboxes, config.rows, config.withColors, config.withAlt, config));
  });
}

const colors = {
  d: { backgroundColor: '#e07e63', color: '#000000' },
  h: { backgroundColor: '#a1daf8', color: '#000000' },
  t: { backgroundColor: '#d1dc6d', color: '#000000' },
  E: { backgroundColor: '#ffed00', color: '#000000' },
  T: { backgroundColor: '#95c11f', color: '#000000' },
  H: { backgroundColor: '#009fe3', color: '#000000' },
  D: { backgroundColor: '#e30613', color: '#FFFFFF' },
};

const bases = ['kg', 'l', 'm', 'm²', 'm³'];

const alt = {
  '100 m²': 'ha',
  '10 m²': 'a',
  'm²': 'ca',
  '10 m': 'dam',
  '100 m': 'hm',
};

function createContentMaker(type) {
  switch (type) {
    case 'position':
      return createPositionTable();
      break;

    case 'weight':
      return createWeightTable();
      break;

    case 'length':
      return createLengthTable();
      break;

    case 'surface':
      return createSurfaceTable();
      break;

    case 'content':
      return createContentTable();
      break;

    case 'volume':
      return createVolumeTable();
      break;
  }
}

function createPositionTable() {
  return ['MD', 'HM', 'TM', 'M', 'HD', 'TD', 'D', 'H', 'T', 'E', ',', 't', 'h', 'd'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createWeightTable() {
  return ['ton', '100 kg', '10 kg', 'kg', '100 g', '10 g', 'g'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createLengthTable() {
  return ['km', '100 m', '10 m', 'm', 'dm', 'cm', 'mm'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createSurfaceTable() {
  return ['km²', '100 m²', '10 m²', 'm²', 'dm²', 'cm²', 'mm²'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createContentTable() {
  return ['100 l', '10 l', 'l', 'dl', 'cl', 'ml'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createVolumeTable() {
  return ['m³', 'dm³', 'cm³', 'mm³'].map((key) => {
    return createInput(key, key, 'checkbox');
  });
}

function createTable(
  config,
  rows: number,
  withColors: boolean,
  withAlt: boolean = false,
  persistedConfig?: PositietabelConfig
) {
  const table = document.createElement('table');
  table.classList.add('cds-lrn-positietabel', 'cds-positietabel', 'cds-lrn-editor-table', 'table', 'table-bordered', 'lrn_width_auto');
  if (persistedConfig) table.setAttribute('data-cb-config', JSON.stringify(persistedConfig));

  const row = document.createElement('tr');

  Object.entries(config)
    .filter(([key, value]) => value)
    .forEach(([key, value]) => {
      const cell = document.createElement('th');
      if (key.includes('²')) {
        cell.colSpan = 2;
      }
      if (key.includes('³')) {
        cell.colSpan = 3;
      }
      cell.classList.add('text-center');

      if (withColors && colors[key]) {
        cell.style.backgroundColor = colors[key].backgroundColor;
        cell.style.color = colors[key].color;
      } else if (withColors && bases.includes(key)) {
        cell.classList.add('primary-container');
      }

      const span = document.createElement('span');
      span.classList.add('title-large');
      span.innerHTML = key === ',' ? '' : key;

      cell.append(span);

      if (withAlt && alt[key]) {
        const altSpan = document.createElement('span');
        if (['dam', 'hm'].includes(alt[key])) {
          altSpan.classList.add('label-small');
        }
        altSpan.innerHTML = '<br/>(' + alt[key] + ')';
        cell.append(altSpan);
      }

      row.append(cell);
    });
  table.append(row);

  for (let i = 0; i < rows; i++) {
    const row = document.createElement('tr');
    Object.entries(config)
      .filter(([key, value]) => value)
      .forEach(([key, value]) => {
        const cell = document.createElement('td');
        cell.innerHTML = key === ',' ? '' : 'placeholder';
        row.append(cell);
        if (key.includes('²') || key.includes('³')) {
          const cell = document.createElement('td');
          cell.innerHTML = 'placeholder';
          row.append(cell);
        }
        if (key.includes('³')) {
          const cell = document.createElement('td');
          cell.innerHTML = 'placeholder';
          row.append(cell);
        }
      });
    table.append(row);
  }

  return table.outerHTML;
}
